/**
 * Payroll Module Types
 * Types for earning profiles, deduction profiles, LNI codes, and payroll operations
 */

// ==================== Employee Types ====================

export type EmployeeType = "shop" | "office";

export interface EmployeeTypeConfig {
  type: EmployeeType;
  label: string;
  description: string;
  defaultGLCategory: "cogs" | "expense";
}

export const EMPLOYEE_TYPES: EmployeeTypeConfig[] = [
  {
    type: "shop",
    label: "Shop",
    description: "Production/manufacturing employees",
    defaultGLCategory: "cogs",
  },
  {
    type: "office",
    label: "Office",
    description: "Administrative/office employees",
    defaultGLCategory: "expense",
  },
];

// ==================== Earning Profile Types ====================

export type EarningType =
  | "salary"
  | "hourly"
  | "bonus"
  | "holiday"
  | "vacation"
  | "sick"
  | "overtime"
  | "double_time"
  | "pto"
  | "commission"
  | "reimbursement"
  | "other";

export interface EarningTypeConfig {
  type: EarningType;
  label: string;
  description: string;
  isRecurring: boolean;
}

export const EARNING_TYPES: EarningTypeConfig[] = [
  { type: "salary", label: "Salary", description: "Regular salary pay", isRecurring: true },
  { type: "hourly", label: "Hourly", description: "Hourly wage pay", isRecurring: true },
  { type: "bonus", label: "Bonus", description: "One-time or periodic bonus", isRecurring: false },
  { type: "holiday", label: "Holiday", description: "Holiday pay", isRecurring: false },
  { type: "vacation", label: "Vacation", description: "Vacation pay", isRecurring: false },
  { type: "sick", label: "Sick", description: "Sick leave pay", isRecurring: false },
  { type: "overtime", label: "Overtime", description: "Overtime pay (1.5x)", isRecurring: false },
  { type: "double_time", label: "Double Time", description: "Double time pay (2x)", isRecurring: false },
  { type: "pto", label: "PTO", description: "Paid time off", isRecurring: false },
  { type: "commission", label: "Commission", description: "Sales commission", isRecurring: false },
  { type: "reimbursement", label: "Reimbursement", description: "Expense reimbursement", isRecurring: false },
  { type: "other", label: "Other", description: "Other earnings", isRecurring: false },
];

export interface EarningProfile {
  earningProfileId: string;
  name: string;
  description?: string;
  companyId: string;
  plantId?: string;
  isActive: boolean;
  isDefault: boolean;
  lineItems: EarningLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface EarningLineItem {
  earningLineItemId: string;
  earningProfileId: string;
  earningCode: string;
  earningType: EarningType;
  description: string;
  glAccountId: string;
  glAccountNumber?: string;
  glAccountName?: string;
  showEveryTime: boolean;
  isActive: boolean;
  sortOrder: number;
  multiplier?: number; // For overtime (1.5) or double time (2.0)
  createdAt: string;
  updatedAt: string;
}

export interface EarningProfileData {
  name: string;
  description?: string;
  companyId: string;
  plantId?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface EarningLineItemData {
  earningProfileId: string;
  earningCode: string;
  earningType: EarningType;
  description: string;
  glAccountId: string;
  showEveryTime: boolean;
  isActive?: boolean;
  sortOrder: number;
  multiplier?: number;
}

// ==================== Deduction Profile Types ====================

export type DeductionType =
  | "federal_tax"
  | "state_tax"
  | "local_tax"
  | "social_security"
  | "medicare"
  | "lni"
  | "paid_family_leave"
  | "long_term_care"
  | "medical"
  | "dental"
  | "vision"
  | "life_insurance"
  | "disability"
  | "401k"
  | "401k_match"
  | "hsa"
  | "fsa"
  | "garnishment"
  | "child_support"
  | "union_dues"
  | "loan_repayment"
  | "other";

export interface DeductionTypeConfig {
  type: DeductionType;
  label: string;
  description: string;
  hasEmployerPortion: boolean;
  isGovernmentMandated: boolean;
  reportingCode?: string; // For 941/W-2 reporting
}

export const DEDUCTION_TYPES: DeductionTypeConfig[] = [
  { type: "federal_tax", label: "Federal Tax", description: "Federal income tax withholding", hasEmployerPortion: false, isGovernmentMandated: true, reportingCode: "FED" },
  { type: "state_tax", label: "State Tax", description: "State income tax withholding", hasEmployerPortion: false, isGovernmentMandated: true, reportingCode: "STATE" },
  { type: "local_tax", label: "Local Tax", description: "Local/city tax withholding", hasEmployerPortion: false, isGovernmentMandated: true },
  { type: "social_security", label: "Social Security", description: "Social Security (FICA)", hasEmployerPortion: true, isGovernmentMandated: true, reportingCode: "SS" },
  { type: "medicare", label: "Medicare", description: "Medicare tax", hasEmployerPortion: true, isGovernmentMandated: true, reportingCode: "MED" },
  { type: "lni", label: "L&I", description: "Labor & Industries (WA workers comp)", hasEmployerPortion: true, isGovernmentMandated: true, reportingCode: "LNI" },
  { type: "paid_family_leave", label: "Paid Family Leave", description: "WA Paid Family & Medical Leave", hasEmployerPortion: true, isGovernmentMandated: true, reportingCode: "PFML" },
  { type: "long_term_care", label: "Long Term Care", description: "WA Long Term Care (WA Cares)", hasEmployerPortion: false, isGovernmentMandated: true, reportingCode: "LTC" },
  { type: "medical", label: "Medical Insurance", description: "Health insurance premium", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "dental", label: "Dental Insurance", description: "Dental insurance premium", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "vision", label: "Vision Insurance", description: "Vision insurance premium", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "life_insurance", label: "Life Insurance", description: "Life insurance premium", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "disability", label: "Disability Insurance", description: "Short/long term disability", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "401k", label: "401(k)", description: "401(k) employee contribution", hasEmployerPortion: false, isGovernmentMandated: false },
  { type: "401k_match", label: "401(k) Match", description: "401(k) employer match", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "hsa", label: "HSA", description: "Health Savings Account", hasEmployerPortion: true, isGovernmentMandated: false },
  { type: "fsa", label: "FSA", description: "Flexible Spending Account", hasEmployerPortion: false, isGovernmentMandated: false },
  { type: "garnishment", label: "Garnishment", description: "Wage garnishment", hasEmployerPortion: false, isGovernmentMandated: true },
  { type: "child_support", label: "Child Support", description: "Child support withholding", hasEmployerPortion: false, isGovernmentMandated: true },
  { type: "union_dues", label: "Union Dues", description: "Union membership dues", hasEmployerPortion: false, isGovernmentMandated: false },
  { type: "loan_repayment", label: "Loan Repayment", description: "Employee loan repayment", hasEmployerPortion: false, isGovernmentMandated: false },
  { type: "other", label: "Other", description: "Other deductions", hasEmployerPortion: false, isGovernmentMandated: false },
];

export interface DeductionProfile {
  deductionProfileId: string;
  name: string;
  description?: string;
  companyId: string;
  employeeType: EmployeeType | "all";
  isActive: boolean;
  isDefault: boolean;
  lineItems: DeductionLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface DeductionLineItem {
  deductionLineItemId: string;
  deductionProfileId: string;
  deductionCode: string;
  deductionType: DeductionType;
  description: string;

  // GL Routing - Employee portion
  employeeGlAccountId: string;
  employeeGlAccountNumber?: string;
  employeeGlAccountName?: string;

  // GL Routing - Employer portion
  employerGlAccountId?: string;
  employerGlAccountNumber?: string;
  employerGlAccountName?: string;

  // Shop vs Office GL Override (KEY REQUIREMENT)
  // When set, shop employees use these GLs instead of the default
  shopEmployeeGlAccountId?: string;
  shopEmployerGlAccountId?: string;

  showEveryTime: boolean;
  isActive: boolean;
  sortOrder: number;

  // Calculation fields (used by Every, stored for reference)
  calculationType?: "percentage" | "fixed" | "tiered";
  rate?: number;
  maxAmount?: number;
  annualLimit?: number;

  createdAt: string;
  updatedAt: string;
}

export interface DeductionProfileData {
  name: string;
  description?: string;
  companyId: string;
  employeeType: EmployeeType | "all";
  isActive?: boolean;
  isDefault?: boolean;
}

export interface DeductionLineItemData {
  deductionProfileId: string;
  deductionCode: string;
  deductionType: DeductionType;
  description: string;
  employeeGlAccountId: string;
  employerGlAccountId?: string;
  shopEmployeeGlAccountId?: string;
  shopEmployerGlAccountId?: string;
  showEveryTime: boolean;
  isActive?: boolean;
  sortOrder: number;
  calculationType?: "percentage" | "fixed" | "tiered";
  rate?: number;
  maxAmount?: number;
  annualLimit?: number;
}

// ==================== LNI Code Types ====================

export interface LNICode {
  lniCodeId: string;
  code: string;
  description: string;
  riskClass: string;
  baseRate: number;
  employeeRate: number;
  employerRate: number;
  effectiveDate: string;
  expirationDate?: string;
  isOffSite: boolean;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LNICodeData {
  code: string;
  description: string;
  riskClass: string;
  baseRate: number;
  employeeRate: number;
  employerRate: number;
  effectiveDate: string;
  expirationDate?: string;
  isOffSite?: boolean;
  isActive?: boolean;
  notes?: string;
}

export interface EmployeeLNIAssignment {
  assignmentId: string;
  workerId: string;
  workerName?: string;
  lniCodeId: string;
  lniCode?: string;
  lniDescription?: string;
  isPrimary: boolean;
  isTemporary: boolean;
  effectiveDate: string;
  expirationDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeLNIAssignmentData {
  workerId: string;
  lniCodeId: string;
  isPrimary: boolean;
  isTemporary: boolean;
  effectiveDate: string;
  expirationDate?: string;
  notes?: string;
}

export interface PayPeriodLNIEntry {
  entryId: string;
  workerId: string;
  workerName?: string;
  payPeriodId: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  lniCodeId: string;
  lniCode?: string;
  hours: number;
  rate: number;
  employeeAmount: number;
  employerAmount: number;
  isTemporary: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PayPeriodLNIEntryData {
  workerId: string;
  payPeriodId: string;
  lniCodeId: string;
  hours: number;
  isTemporary: boolean;
  notes?: string;
}

// ==================== LNI Calculation Types ====================

export interface LNICalculationInput {
  workerId: string;
  payPeriodId: string;
  totalHours: number;
  temporaryLNIEntries: {
    lniCodeId: string;
    hours: number;
  }[];
}

export interface LNICalculationResult {
  workerId: string;
  payPeriodId: string;
  totalHours: number;
  primaryLNI: {
    lniCodeId: string;
    lniCode: string;
    hours: number;
    employeeAmount: number;
    employerAmount: number;
  };
  temporaryEntries: {
    lniCodeId: string;
    lniCode: string;
    hours: number;
    employeeAmount: number;
    employerAmount: number;
  }[];
  totals: {
    totalEmployeeAmount: number;
    totalEmployerAmount: number;
    totalAmount: number;
  };
}

// ==================== GL Routing Types ====================

export interface GLRoutingConfig {
  deductionCode: string;
  deductionType: DeductionType;
  employeeType: EmployeeType;
  portion: "employee" | "employer";
  glAccountId: string;
  glAccountNumber: string;
  glAccountName: string;
}

export interface GLRoutingResult {
  deductionCode: string;
  employeeGlAccountId: string;
  employerGlAccountId?: string;
}

export interface DeductionReportTotal {
  deductionCode: string;
  deductionType: DeductionType;
  reportingCode?: string;
  totalEmployeePortion: number;
  totalEmployerPortion: number;
  totalAmount: number;
  // No shop/office split - unified for government reporting (941, W-2)
}

// ==================== Worker Profile Assignment Types ====================

export interface WorkerProfileAssignment {
  workerId: string;
  earningProfileId?: string;
  deductionProfileId?: string;
  employeeType: EmployeeType;
  primaryLNICodeId?: string;
  plantId?: string;
}

// ==================== Pay Period Types ====================

export interface PayPeriod {
  payPeriodId: string;
  companyId: string;
  startDate: string;
  endDate: string;
  payDate: string;
  status: "open" | "processing" | "closed";
  createdAt: string;
  updatedAt: string;
}

// ==================== Payroll Entry Types ====================

export interface PayrollEntry {
  entryId: string;
  workerId: string;
  workerName: string;
  payPeriodId: string;
  plantId: string;
  employeeType: EmployeeType;

  // Earnings
  earnings: PayrollEarningEntry[];
  totalEarnings: number;

  // Deductions
  deductions: PayrollDeductionEntry[];
  totalEmployeeDeductions: number;
  totalEmployerDeductions: number;

  // LNI
  lniEntries: PayPeriodLNIEntry[];
  totalLNIEmployee: number;
  totalLNIEmployer: number;

  // Net
  netPay: number;

  status: "draft" | "pending" | "approved" | "paid";
  createdAt: string;
  updatedAt: string;
}

export interface PayrollEarningEntry {
  earningCode: string;
  earningType: EarningType;
  description: string;
  hours?: number;
  rate?: number;
  amount: number;
  glAccountId: string;
}

export interface PayrollDeductionEntry {
  deductionCode: string;
  deductionType: DeductionType;
  description: string;
  employeeAmount: number;
  employerAmount: number;
  employeeGlAccountId: string;
  employerGlAccountId?: string;
}

// ==================== Vending Integration Types (Phase 5) ====================

export interface VendingTransaction {
  transactionId: string;
  workerId: string;
  workerName?: string;
  machineId: string;
  machineName?: string;
  itemCode: string;
  itemDescription: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  transactionDate: string;
  transactionTime: string;
  isFreeAllocation: boolean;
  isProcessed: boolean;
  payPeriodId?: string;
  createdAt: string;
}

export interface VendingDeductionSummary {
  workerId: string;
  workerName: string;
  payPeriodId: string;
  transactions: VendingTransaction[];
  totalAmount: number;
  freeAllocationUsed: number;
  chargeableAmount: number;
}
